import type { RegisterDraft } from './SampleNewScreen.tsx'

/**
 * 시료 추가 — 입력 폼 하나 + 담은 목록(eric 2026-10-04 「시료 번호랑 환자 번호, 타입을 위에서 입력 받고.. 밑에 추가
 * 버튼을 누르고.. 아래는.. 빼거나 편집만 보이게 해.. 지금 입력이 여러 곳이 분산되어 있잖아」).
 *
 * 🔵 항목 → 보내는 곳(`RegisterSampleBody` · 2026-10-04 재구성 그대로):
 *    | 시료 번호   | `sample_id`(필수)                                                        |
 *    | 환자 번호   | `patient_id`                                                             |
 *    | 시료 타입   | `sample_type` — 자유 문자열(DDL `TEXT` · 계약 enum 없음 · dev2 실측 2026-10-04) |
 *    | 채혈 일시   | 날짜 → `blood_draw_date`(DATE) · 시각까지 → `metadata.blood_draw_at`(BR-59 임시)  |
 *    | 암종 · 병기 | `cancer_type` · `cancer_stage` 에 합친 글자(`, `) + `metadata.cancer_types` · `cancer_stages` 배열(BR-113 임시) |
 *    | 추가 입력   | `metadata` 의 이름:값(숫자면 숫자)                                        |
 * 🔴 **적혈구 농도 전용 칸(`rbc_concentration` 열)은 없다**(eric 2026-10-04) — 추가 입력의 예시로만 선다.
 * 🔴 **입력은 위 폼 한 곳이다.** 담은 목록은 읽기 전용이고 [편집](폼으로 올린다) · [빼기]만 있다.
 *    종전 엑셀식 표(칸 안 편집 · 칸 이동 · 늘 빈 줄 · 공통 값 구역)는 걷었다 — 되살리지 마라.
 *    엑셀 붙여넣기는 폼에 바로 한다(단추는 걷었다 · eric 2026-10-12) — 붙인 여러 줄이 목록에 바로 담기고, 틀린 줄은 ⚠ + 사유로 서서 [편집] 으로 고친다.
 */

/** 화면이 metadata 에 쓰는 이름 — 추가 입력이 같은 이름을 쓰면 덮이므로 막는다 */
export const META_DRAW_AT = 'blood_draw_at'
export const META_CANCER_TYPES = 'cancer_types'
export const META_CANCER_STAGES = 'cancer_stages'
export const RESERVED_META_KEYS: readonly string[] = [
  META_DRAW_AT,
  META_CANCER_TYPES,
  META_CANCER_STAGES,
]

/**
 * 여러 값을 한 칸(`cancer_type` · `cancer_stage` TEXT)에 합칠 때의 구분자. 사람이 적고 붙여 넣는 꼴(쉼표)과
 * 같아야 읽은 값이 다시 같은 칩으로 갈린다(`splitTags`).
 */
export const TAG_JOIN = ', '

/** 추가 입력 한 항목(metadata 의 이름:값). `id` 는 화면 안 열쇠다 — 보내지 않는다 */
export interface ExtraEntry {
  id: string
  key: string
  value: string
}

/**
 * 담은 목록의 열 — 차례가 곧 붙여넣기의 열 차례다(엑셀에서 이 차례로 복사해 온다).
 * ⚠ 폼 · 카드의 보이는 차례(시료 번호 · 타입 · 환자 번호 … · eric 2026-10-13)와는 다르다 — 그쪽은 `SampleNewScreen` 의
 *   `CARD_COLUMNS` 다. 이 차례는 사용자 자료 모양이라 화면 차례를 따라 바꾸지 않는다.
 */
export const LIST_COLUMNS = [
  'sample_id',
  'patient_id',
  'sample_type',
  'blood_draw',
  'cancer_type',
  'cancer_stage',
  'extras',
] as const

export type ListColumn = (typeof LIST_COLUMNS)[number]

/** 여러 값(칩) 열 */
export type TagColumn = 'cancer_type' | 'cancer_stage'

/** 줄의 등록 결과. 🔴 서버 응답으로만 선다 — 보내기 전에 「됐다」로 그리지 않는다(DP-04) */
export type RowResult =
  /** `withMetadata` — metadata 를 실어 보냈다. 서버가 응답에 안 돌려줘 화면이 받은 값을 못 보인다(BR-59 ②) */
  | { kind: 'ok'; withMetadata: boolean }
  /** 409 `SAMPLE_ALREADY_EXISTS` — 그 번호 칸에 적는다 */
  | { kind: 'duplicate'; message: string; sampleId: string }
  | { kind: 'failed'; message: string }

/** 담은 시료 한 줄 */
export interface SampleEntry {
  /** 재등록 원본의 POST 임상 값. 일반 등록에는 전용 입력칸을 만들지 않는다. */
  rbc_concentration?: string

  key: string
  sample_id: string
  patient_id: string
  sample_type: string
  /** `YYYY-MM-DD` 또는 `YYYY-MM-DD HH:MM` — 붙여 넣은 줄은 틀린 꼴일 수 있다(목록에 ⚠ 로 선다) */
  blood_draw: string
  cancer_type: readonly string[]
  cancer_stage: readonly string[]
  extras: readonly ExtraEntry[]
  /**
   * 멱등키(IFC-MSG-04)와 그때 보낸 본문의 지문. 🔴 **같은 본문을 다시 보낼 때만 같은 키를 쓴다** —
   * 응답을 못 받은 등록을 다시 보내면 서버가 첫 결과를 재생한다. 본문이 바뀌면 새 키다.
   */
  sent?: { commandId: string; fingerprint: string } | undefined
  result?: RowResult | undefined
}

/** 위 입력 폼 */
export interface SampleForm {
  rbc_concentration?: string

  sample_id: string
  patient_id: string
  sample_type: string
  /** `YYYY-MM-DD` (날짜 고르개) */
  draw_date: string
  /** `HH:MM` — 비면 날짜만 */
  draw_time: string
  cancer_type: readonly string[]
  cancer_stage: readonly string[]
  extras: readonly ExtraEntry[]
}

export const EMPTY_FORM: SampleForm = {
  sample_id: '',
  patient_id: '',
  sample_type: '',
  draw_date: '',
  draw_time: '',
  cancer_type: [],
  cancer_stage: [],
  extras: [],
}

let entrySerial = 0
let extraSerial = 0
/*
 * 모듈이 읽힐 때마다 바뀌는 표지 — 개발 서버가 이 파일만 다시 읽으면(HMR) 일련번호가 0 으로 돌아가, 화면에 남은 줄과 새 줄이 같은 열쇠를
 * 받아 추가 입력 두 줄이 같이 바뀌었다(eric 2026-10-20 「추가 입력.. 두개.. 같이 바껴」). 표지를 얹어 다시 읽혀도 겹치지 않게 한다
 */
const LOAD_TAG = Math.random().toString(36).slice(2, 8)

export function newEntry(fields: Partial<Omit<SampleEntry, 'key'>> = {}): SampleEntry {
  entrySerial += 1
  return {
    key: `entry-${LOAD_TAG}-${String(entrySerial)}`,
    sample_id: '',
    patient_id: '',
    sample_type: '',
    blood_draw: '',
    cancer_type: [],
    cancer_stage: [],
    extras: [],
    ...fields,
  }
}

export function newExtra(key = '', value = ''): ExtraEntry {
  extraSerial += 1
  return { id: `extra-${LOAD_TAG}-${String(extraSerial)}`, key, value }
}

const isBlankExtra = (e: ExtraEntry) => e.key.trim() === '' && e.value.trim() === ''

/** 보낼 줄인가 — 등록되지 않은 줄 */
export function isPendingEntry(entry: SampleEntry): boolean {
  return entry.result?.kind !== 'ok'
}

/**
 * 폼 → 담을 줄. 🔴 폼 검증(시료 번호 · 채혈 일시 · 추가 입력)을 통과한 뒤에만 부른다.
 * 빈 추가 입력 항목(이름 · 값 둘 다 빈)은 담지 않는다.
 */
export function entryFromForm(form: SampleForm): SampleEntry {
  const draw = formDraw(form)
  const fields = {
    ...(form.rbc_concentration === undefined ? {} : { rbc_concentration: form.rbc_concentration }),
    sample_id: form.sample_id.trim(),
    patient_id: form.patient_id.trim(),
    sample_type: form.sample_type.trim(),
    blood_draw: draw === null || draw === 'invalid' ? '' : drawText(draw),
    cancer_type: [...form.cancer_type],
    cancer_stage: [...form.cancer_stage],
    extras: form.extras.filter((e) => !isBlankExtra(e)),
  }
  return newEntry(fields)
}

/**
 * 담은 뒤 다음 시료를 위한 폼 — **타입만 남기고 다 비운다**(eric 2026-10-20 「채혈 일시라던가. 암종 암 병기.. 리셋해줘야지」).
 * 타입은 필수 칸이고 같은 묶음이면 대개 같다. 종전엔 채혈 일시 · 암종 · 병기도 남겼다.
 */
export function formForNext(form: SampleForm): SampleForm {
  return { ...EMPTY_FORM, sample_type: form.sample_type }
}

/* ───────────── 여러 값(칩) ───────────── */

/** 「위암, 폐암」 → `['위암', '폐암']`. 쉼표(전각 · 「、」 포함) · 줄바꿈으로 가른다 */
export function splitTags(text: string): string[] {
  return text
    .split(/[,，、\n]/)
    .map((s) => s.trim())
    .filter((s) => s !== '')
}

/* ───────────── 채혈 일시 ───────────── */

/** 채혈 일시 — 날짜는 늘, 시각은 있을 때만(`HH:MM` · 초가 적혔으면 `HH:MM:SS`) */
export interface DrawAt {
  date: string
  time?: string | undefined
}

const pad2 = (s: string) => s.padStart(2, '0')

/** `YYYY-MM-DD` 이고 실제 있는 날인가(2026-02-30 은 아니다) */
export function isIsoDay(value: string): boolean {
  const hit = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (hit === null) return false
  const [y, m, d] = [Number(hit[1]), Number(hit[2]), Number(hit[3])]
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

/**
 * 시각 글 → `HH:MM`(초가 0 이 아니면 `HH:MM:SS`). 24시간제만. 꼴이 아니면 `null`.
 * 🔴 **값을 지어내지 않는다** — 오전/오후 · 「9시 반」 같은 꼴은 읽지 않고 칸 오류로 둔다.
 */
export function normalizeTime(raw: string): string | null {
  const hit = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(raw.trim())
  if (hit === null) return null
  const [h, m, s] = [Number(hit[1]), Number(hit[2]), hit[3] === undefined ? 0 : Number(hit[3])]
  if (h > 23 || m > 59 || s > 59) return null
  const hm = `${pad2(String(h))}:${pad2(String(m))}`
  return s === 0 ? hm : `${hm}:${pad2(String(s))}`
}

/**
 * 붙여 넣거나 적은 채혈 일시의 구분자만 고른다 — `2026/10/3 9:30` · `2026. 10. 3. 09:30` · `2026-10-03T09:30`
 * → `2026-10-03 09:30`. 꼴이 아니면 그대로 두고, 칸 검증이 「형식」으로 잡는다.
 */
export function normalizeDraw(raw: string): string {
  const text = raw.trim()
  const hit = /^(\d{4})\s*[-./]\s*(\d{1,2})\s*[-./]\s*(\d{1,2})\.?(?:\s*[T\s]\s*(\S+))?$/.exec(text)
  if (hit === null) return text
  const day = `${hit[1] ?? ''}-${pad2(hit[2] ?? '')}-${pad2(hit[3] ?? '')}`
  if (hit[4] === undefined) return day
  const time = normalizeTime(hit[4])
  return time === null ? text : `${day} ${time}`
}

/** 채혈 일시 칸 → 값. 빈 칸 `null` · 꼴이 틀리면 `'invalid'` */
export function parseDraw(raw: string): DrawAt | null | 'invalid' {
  const text = normalizeDraw(raw)
  if (text === '') return null
  const hit = /^(\d{4}-\d{2}-\d{2})(?: (\d{2}:\d{2}(?::\d{2})?))?$/.exec(text)
  if (hit === null || !isIsoDay(hit[1] ?? '')) return 'invalid'
  return { date: hit[1] ?? '', time: hit[2] }
}

/** 입력 폼의 채혈 일시 — 날짜 없이 시각만 있거나 시각 꼴이 틀리면 `'invalid'` */
export function formDraw(form: SampleForm): DrawAt | null | 'invalid' {
  const date = form.draw_date.trim()
  const timeRaw = form.draw_time.trim()
  if (date === '' && timeRaw === '') return null
  if (date === '' || !isIsoDay(date)) return 'invalid'
  if (timeRaw === '') return { date }
  const time = normalizeTime(timeRaw)
  return time === null ? 'invalid' : { date, time }
}

/** 입력 폼 채혈 일시의 칸 오류 — 화면이 그 칸에 적는다 */
export function formDrawIssue(form: SampleForm): 'timeFormat' | 'noDate' | undefined {
  const timeRaw = form.draw_time.trim()
  if (timeRaw === '') return undefined
  if (normalizeTime(timeRaw) === null) return 'timeFormat'
  return form.draw_date.trim() === '' ? 'noDate' : undefined
}

/** 화면 표기 — `2026-10-04 09:30` */
export function drawText(draw: DrawAt): string {
  return draw.time === undefined ? draw.date : `${draw.date} ${draw.time}`
}

/**
 * 표시 시간대의 그 순간 UTC 차 — `+09:00`. 🔴 숫자를 박지 않는다(`DISPLAY_TIME_ZONE` 이 정한다).
 * 서울은 일광 절약이 없어 하루 안에서 갈리지 않지만, 그래도 그 날짜 · 시각의 차를 읽는다.
 */
function zoneOffset(asIfUtcMs: number, timeZone: string): string {
  const name =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
      .formatToParts(new Date(asIfUtcMs))
      .find((p) => p.type === 'timeZoneName')?.value ?? 'GMT'
  const hit = /([+-]\d{2}):?(\d{2})/.exec(name)
  return hit === null ? '+00:00' : `${hit[1] ?? '+00'}:${hit[2] ?? '00'}`
}

/** 채혈 일시 → `metadata.blood_draw_at` 값(`2026-10-04T09:30:00+09:00`). 시각이 없으면 `undefined` */
export function drawAtIso(draw: DrawAt, timeZone: string): string | undefined {
  if (draw.time === undefined) return undefined
  const [h = '00', m = '00', s = '00'] = draw.time.split(':')
  const [y, mo, d] = draw.date.split('-').map(Number)
  const asIfUtc = Date.UTC(y ?? 0, (mo ?? 1) - 1, d ?? 1, Number(h), Number(m), Number(s))
  return `${draw.date}T${h}:${m}:${s}${zoneOffset(asIfUtc, timeZone)}`
}

/* ───────────── 추가 입력(metadata) ───────────── */

/**
 * 추가 입력의 값 → metadata 값. 「숫자면 숫자로, 아니면 글자」(팀장 2026-10-04).
 * `4.5` · `-1` · `6,500`(천 단위 쉼표) 은 숫자다. `1e3` · `0x10` 은 사람이 적는 수치가 아니라 글자로 둔다.
 */
export function extraValue(raw: string): number | string {
  const text = raw.trim()
  if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text)
  if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) return Number(text.replace(/,/g, ''))
  return text
}

export type ExtrasProblem = 'noKey' | 'noValue' | 'dupKey' | 'reserved'

/** 추가 입력의 문제 — 첫 문제와 그 항목들. 빈 항목(이름 · 값 둘 다 빈)은 문제가 아니다(안 보낸다) */
export function extrasIssue(
  entries: readonly ExtraEntry[],
): { problem: ExtrasProblem; ids: Set<string>; key?: string } | undefined {
  const live = entries.filter((e) => !isBlankExtra(e))
  const noKey = live.filter((e) => e.key.trim() === '')
  if (noKey.length > 0) return { problem: 'noKey', ids: new Set(noKey.map((e) => e.id)) }
  const noValue = live.filter((e) => e.value.trim() === '')
  if (noValue.length > 0) return { problem: 'noValue', ids: new Set(noValue.map((e) => e.id)) }
  const reserved = live.find((e) => RESERVED_META_KEYS.includes(e.key.trim()))
  if (reserved !== undefined)
    return { problem: 'reserved', ids: new Set([reserved.id]), key: reserved.key.trim() }
  const seen = new Map<string, string>()
  for (const e of live) {
    const key = e.key.trim()
    const first = seen.get(key)
    if (first !== undefined) return { problem: 'dupKey', ids: new Set([first, e.id]), key }
    seen.set(key, e.id)
  }
  return undefined
}

/** 칸 요약 — 「적혈구 4.5 · 혈청 황색」 */
export function extrasSummary(entries: readonly ExtraEntry[]): string {
  return entries
    .filter((e) => !isBlankExtra(e))
    .map((e) => `${e.key.trim()} ${e.value.trim()}`.trim())
    .join(' · ')
}

/**
 * 붙여 넣은 추가 입력 글 → 항목. `적혈구=4.5; 혈청=황색` (`=` 또는 `:` · `;` · 줄바꿈으로 가른다).
 * 쉼표는 **다음 조각에 `=`/`:` 가 있을 때만** 가른다 — `백혈구=6,500` 의 천 단위 쉼표를 자르지 않게.
 * 🔴 이름 · 값을 지어내지 않는다 — 짝이 안 맞는 조각은 그대로 항목이 되고 칸 오류(이름 · 값 없음)로 선다.
 */
export function parseExtrasText(text: string): ExtraEntry[] {
  return text
    .split(/[;；\n]|,(?=[^,;；\n]*[=:])/)
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '')
    .map((piece) => {
      const at = piece.search(/[=:]/)
      return at < 0
        ? newExtra(piece, '')
        : newExtra(piece.slice(0, at).trim(), piece.slice(at + 1).trim())
    })
}

/* ───────────── 줄 → 보낼 본문 ───────────── */

/** 이 본문이 metadata 를 싣나 — 결과 칸이 「응답에 없음」을 말할지 가른다 */
export function hasMetadata(draft: RegisterDraft): boolean {
  return draft.metadata !== undefined && Object.keys(draft.metadata).length > 0
}

/**
 * 줄 → 등록 본문 초안. 🔴 검증을 통과한 줄만 여기 온다 — 채혈 일시가 틀린 줄은 보내기 전에 막힌다.
 * metadata 는 **실을 것이 있을 때만** 키가 선다(빈 객체를 보내지 않는다).
 */
export function entryDraft(entry: SampleEntry, timeZone: string): RegisterDraft {
  const draw = parseDraw(entry.blood_draw)
  const metadata: Record<string, unknown> = {}
  for (const e of entry.extras) {
    if (isBlankExtra(e)) continue
    metadata[e.key.trim()] = extraValue(e.value)
  }
  const at = draw === null || draw === 'invalid' ? undefined : drawAtIso(draw, timeZone)
  if (at !== undefined) metadata[META_DRAW_AT] = at
  if (entry.cancer_type.length > 0) metadata[META_CANCER_TYPES] = [...entry.cancer_type]
  if (entry.cancer_stage.length > 0) metadata[META_CANCER_STAGES] = [...entry.cancer_stage]
  return {
    ...(entry.rbc_concentration === undefined
      ? {}
      : { rbc_concentration: entry.rbc_concentration }),
    sample_id: entry.sample_id.trim(),
    patient_id: entry.patient_id,
    sample_type: entry.sample_type,
    blood_draw_date: draw === null || draw === 'invalid' ? '' : draw.date,
    cancer_type: entry.cancer_type.join(TAG_JOIN),
    cancer_stage: entry.cancer_stage.join(TAG_JOIN),
    ...(Object.keys(metadata).length === 0 ? {} : { metadata }),
  }
}

/** 보낼 본문의 지문 — 멱등키를 다시 쓸지 가른다 */
export function draftFingerprint(draft: RegisterDraft): string {
  return JSON.stringify(draft)
}

/** 목록 안에서 두 줄 이상이 같은 번호(빈 칸 · 앞뒤 공백 무시) */
export function duplicateIds(ids: readonly string[]): Set<string> {
  const seen = new Set<string>()
  const dup = new Set<string>()
  for (const raw of ids) {
    const id = raw.trim()
    if (id === '') continue
    if (seen.has(id)) dup.add(id)
    seen.add(id)
  }
  return dup
}

/* ───────────── 붙여넣기 ───────────── */

/** 붙여 넣은 글 → 칸 격자. 탭 · 줄바꿈 구분(엑셀 · 구글 시트의 복사 꼴). 끝의 빈 줄은 버린다 */
export function parseClipboardGrid(text: string): string[][] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  while (lines.length > 0 && (lines[lines.length - 1] ?? '').trim() === '') lines.pop()
  return lines.map((line) => line.split('\t'))
}

/** 여러 칸 붙여넣기인가 — 탭 · 줄바꿈이 없으면 그 칸의 보통 붙여넣기다 */
export function isGridPaste(text: string): boolean {
  return /[\t\n]/.test(text.replace(/[\r\n]+$/, ''))
}

/**
 * 붙여 넣은 격자 → 담을 줄. 열 차례는 `LIST_COLUMNS`. 넘친 열은 버리고, 칸이 다 빈 줄은 건너뛴다.
 * 첫 줄이 목록 머리글과 같으면(머리글째 복사) 그 줄은 건너뛴다.
 * 🔴 값을 고치지 않는다 — 날짜 구분자만 고르고(`normalizeDraw`), 틀린 줄은 그대로 담겨 목록에 ⚠ 로 선다.
 */
export function entriesFromGrid(
  grid: readonly (readonly string[])[],
  headerLabels: readonly string[],
): SampleEntry[] {
  const first = grid[0]
  const isHeader =
    first !== undefined &&
    first.length > 1 &&
    first.every((cell, j) => cell.trim() === (headerLabels[j] ?? '').trim())
  const body = isHeader ? grid.slice(1) : grid
  const out: SampleEntry[] = []
  for (const line of body) {
    if (line.every((cell) => cell.trim() === '')) continue
    const at = (col: ListColumn) => line[LIST_COLUMNS.indexOf(col)] ?? ''
    out.push(
      newEntry({
        sample_id: at('sample_id').trim(),
        patient_id: at('patient_id').trim(),
        sample_type: at('sample_type').trim(),
        blood_draw: normalizeDraw(at('blood_draw')),
        cancer_type: splitTags(at('cancer_type')),
        cancer_stage: splitTags(at('cancer_stage')),
        extras: parseExtrasText(at('extras')),
      }),
    )
  }
  return out
}
